import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { Box } from "./Box";
import { ColorPicker, colorToRgbString } from "./ColorPicker";
import { IconButton } from "./IconButton";
import { Popper } from "./Popper";
import { DeleteOutline } from "../icons/icons";
import { SlotsProvider } from "../providers/SlotsProvider";
import { Typography } from "./Typography";
export const linearGradientPickerClasses = {
    root: "MinervaLinearGradientPicker-root",
    "marker-container": "MinervaLinearGradientPicker-markerContainer",
    "marker-root": "MinervaLinearGradientPicker-markerRoot",
    "padding-offset-container": "MinervaLinearGradientPicker-paddingOffsetContainer",
    "transparent-background-container": "MinervaLinearGradientPicker-transparentBackgroundContainer",
    "linear-gradient": "MinervaLinearGradientPicker-linearGradient",
    "limit-warning": "MinervaLinearGradientPicker-limitWarning",
};
export const linearGradientPickerSlots = {
    popper: Popper,
    colorPicker: ColorPicker,
    deleteButton: IconButton,
    deleteIcon: DeleteOutline,
    limitWarning: Popper,
};
export const LinearGradientPicker = ({ limit = 10, gradient: _gradient, hideDragPastIndicators = false, onGradientChange, slots, slotProps, sx, className, onMouseDown, ...props }) => {
    const rootRef = useRef(null);
    const gradientRef = useRef(null);
    const gradientBarHeight = 32;
    const edgeBuffer = 12;
    const [dragging, setDragging] = useState(undefined);
    const [colorPickerOpen, setColorPickerOpen] = useState(undefined);
    const [rootWidth, setRootWidth] = useState(0);
    const [gradient, setGradient] = useState(_gradient
        ? _gradient.map((g, index) => ({ ...g, id: index }))
        : [
            { id: 0, offset: 0, color: "white" },
            { id: 1, offset: 1, color: "black" },
        ]);
    const sortGradient = (gradient) => gradient.sort((a, b) => a.offset - b.offset);
    const addColor = (offset) => {
        const node = {
            offset: Number.parseFloat((offset / rootWidth).toFixed(2)),
            color: "black",
            id: gradient.length,
        };
        return {
            node: node,
            gradient: sortGradient([node, ...gradient]),
        };
    };
    const deleteColor = (id) => gradient.filter((g) => g.id !== id);
    const updateGradient = (id, offset, color) => sortGradient(gradient.map((g) => {
        if (g.id === id) {
            return {
                ...g,
                color: color ?? g.color,
                offset: offset ? offset / (rootWidth - edgeBuffer) : g.offset,
            };
        }
        return g;
    }));
    const setGradientRef = (gradient) => {
        gradientRef.current?.style.setProperty("background-image", `linear-gradient(to right, ${gradient
            .map((color) => `${color.color} ${color.offset * 100}%`)
            .join(", ")})`);
    };
    const getNodePositionByOffset = (offset) => {
        return (rootWidth - edgeBuffer) * offset;
    };
    const getDragBounds = (id) => {
        const offset = gradient.find((g) => g.id === id)?.offset;
        const leadingColor = gradient.find((g) => offset && g.disabledNodeDragPast && g.offset < offset);
        const trailingColor = gradient.find((g) => offset && g.disabledNodeDragPast && g.offset > offset);
        return {
            left: leadingColor
                ? getNodePositionByOffset(leadingColor.offset) + edgeBuffer
                : undefined,
            right: trailingColor
                ? getNodePositionByOffset(trailingColor.offset) - edgeBuffer
                : undefined,
        };
    };
    const canAddNode = (offset) => {
        if (offset === undefined) {
            return true;
        }
        const leadingColor = gradient.find((g) => g.disableAddNodeAfter && g.offset < offset);
        const trailingColor = gradient.find((g) => g.disableAddNodeBefore && g.offset > offset);
        return !leadingColor && !trailingColor;
    };
    useEffect(() => {
        setRootWidth(rootRef.current?.offsetWidth ?? 0);
        const resizeObserver = new ResizeObserver((entries) => {
            entries.map((entry) => {
                if (entry.target === rootRef.current) {
                    const newWidth = entry.contentRect.width;
                    setRootWidth(newWidth);
                    setGradientRef(gradient);
                }
            });
        });
        if (rootRef.current) {
            resizeObserver.observe(rootRef.current);
        }
        return () => {
            if (rootRef.current) {
                resizeObserver.unobserve(rootRef.current);
            }
        };
    }, [gradient]);
    useEffect(() => {
        setRootWidth(rootRef.current?.offsetWidth ?? 0);
    }, []);
    useEffect(() => {
        const newGradient = _gradient
            ? _gradient.map((g, index) => ({ ...g, id: index }))
            : [
                { id: 0, offset: 0, color: "white" },
                { id: 1, offset: 1, color: "black" },
            ];
        setGradient(newGradient);
        setGradientRef(newGradient);
    }, [_gradient]);
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Delete" && colorPickerOpen !== undefined && gradient.length > 2) {
                const newGradient = deleteColor(colorPickerOpen);
                setGradientRef(newGradient);
                setGradient(newGradient);
                onGradientChange?.(newGradient);
                setColorPickerOpen(undefined);
            }
        };
        typeof window !== "undefined" && window.addEventListener("keydown", handleKeyDown);
        return () => {
            typeof window !== "undefined" && window.removeEventListener("keydown", handleKeyDown);
        };
    }, [colorPickerOpen]);
    return (_jsx(SlotsProvider, { baseSlots: linearGradientPickerSlots, slots: slots, children: ({ popper: Popper, colorPicker: ColorPicker, deleteButton: DeleteButton, deleteIcon: DeleteIcon, limitWarning: LimitWarning, }) => (_jsxs(Box, { ref: rootRef, width: "100%", sx: {
                display: "flex",
                flexDirection: "column",
                ...sx,
            }, className: clsx([linearGradientPickerClasses.root, className]), onMouseDown: (e) => {
                e.stopPropagation();
                if (gradient.length < limit) {
                    const { node: addedNode, gradient: newGradient } = addColor(e.nativeEvent.offsetX);
                    if (canAddNode(addedNode.offset)) {
                        setGradientRef(newGradient);
                        setGradient(newGradient);
                        onGradientChange?.(newGradient);
                    }
                }
                onMouseDown?.(e);
            }, ...props, children: [_jsx(Box, { width: "100%", height: 4, sx: {
                        display: "flex",
                        position: "relative",
                    }, className: linearGradientPickerClasses["marker-container"], children: gradient.map(({ color, offset, id, disabled, disabledNodeDragPast: disallowDragPast, }) => {
                        const isDisabled = colorPickerOpen !== undefined ||
                            !!(dragging !== undefined && dragging !== id) ||
                            disabled;
                        const { left, right } = getDragBounds(id);
                        return (_jsx(Draggable, { axis: "x", bounds: left || right
                                ? {
                                    left: left ?? 0,
                                    right: right ?? rootWidth - edgeBuffer,
                                }
                                : "parent", disabled: isDisabled, position: {
                                x: getNodePositionByOffset(offset),
                                y: 0,
                            }, onStart: () => {
                                setTimeout(() => {
                                    setDragging(id);
                                }, 150);
                            }, onStop: (_, { x }) => {
                                setTimeout(() => {
                                    setDragging(undefined);
                                }, 150);
                                const newGradient = updateGradient(id, x);
                                setGradient(newGradient);
                                onGradientChange?.(newGradient);
                            }, onDrag: (e, { x }) => {
                                e.stopPropagation();
                                setGradientRef(updateGradient(id, x));
                            }, onMouseDown: (e) => e.stopPropagation(), children: _jsxs(Box, { className: linearGradientPickerClasses["marker-root"], sx: {
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.5,
                                    cursor: disabled
                                        ? "not-allowed"
                                        : dragging
                                            ? "grabbing"
                                            : "pointer",
                                    opacity: dragging !== undefined && dragging !== id
                                        ? 0.6
                                        : 1,
                                }, children: [_jsx(Popper, { open: colorPickerOpen === id, disabled: dragging !== undefined || disabled, content: _jsx(ColorPicker, { topNode: _jsx(DeleteButton, { variant: "ghost", color: "surface", density: "dense", disabled: gradient.length <= 2, ...slotProps?.deleteButton, onClick: (e) => {
                                                    const newGradient = deleteColor(id);
                                                    setGradientRef(newGradient);
                                                    setGradient(newGradient);
                                                    setColorPickerOpen(undefined);
                                                    onGradientChange?.(newGradient);
                                                    slotProps?.deleteButton?.onClick?.(e);
                                                }, children: _jsx(DeleteIcon, { size: "xs", ...slotProps?.deleteIcon }) }), initialColor: color, ...slotProps?.colorPicker, onApply: (newColor) => {
                                                const newGradient = updateGradient(id, undefined, colorToRgbString(newColor.rgb));
                                                setGradientRef(newGradient);
                                                setGradient(newGradient);
                                                setColorPickerOpen(undefined);
                                                onGradientChange?.(newGradient);
                                            } }), ...slotProps?.popper, onOpenChange: (open) => {
                                            setColorPickerOpen(open ? id : undefined);
                                            slotProps?.popper?.onOpenChange?.(open);
                                        }, sx: {
                                            padding: 2,
                                            width: 75,
                                            ...slotProps?.popper?.sx,
                                        }, children: _jsx(Box, { sx: {
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                "&::before": {
                                                    content: '""',
                                                    display: "block",
                                                    width: 2.5,
                                                    height: 2.5,
                                                    border: 1,
                                                    borderColor: (theme) => theme.palette.stroke[100],
                                                    outline: 1,
                                                    outlineOffset: -0.5,
                                                    outlineColor: "white",
                                                    background: color,
                                                },
                                                "&::after": {
                                                    content: '""',
                                                    display: "block",
                                                    height: 0,
                                                    width: 0,
                                                    borderRight: 0.25,
                                                    borderRightColor: "transparent",
                                                    borderLeft: 0.25,
                                                    borderLeftColor: "transparent",
                                                    borderTop: 0.25,
                                                    borderTopColor: (theme) => theme.palette.stroke[100],
                                                },
                                            } }) }), disallowDragPast && !hideDragPastIndicators ? (_jsx(Box, { width: "2px", height: `${gradientBarHeight + 1}px`, sx: {
                                            background: "black",
                                            outline: 2,
                                            outlineColor: "white",
                                        } })) : (_jsx(Box, { width: "2px", height: "2px", sx: {
                                            background: (theme) => theme.palette.stroke[100],
                                            borderRadius: "50%",
                                        } }))] }) }, id));
                    }) }), _jsx(Box, { sx: {
                        paddingX: 1.25,
                        cursor: gradient.length < limit ? "crosshair" : "not-allowed",
                    }, className: linearGradientPickerClasses["padding-offset-container"], children: _jsx(LimitWarning, { disabled: gradient.length < limit, content: _jsxs(Typography, { variant: "body3", align: "center", children: ["Limit of ", limit, " gradient points reached"] }), ...slotProps?.limitWarning, className: clsx([
                            slotProps?.limitWarning?.className,
                            linearGradientPickerClasses["limit-warning"],
                        ]), sx: {
                            maxWidth: 35,
                            padding: 1,
                            ...slotProps?.limitWarning?.sx,
                        }, children: _jsx(Box, { sx: {
                                backgroundImage: "linear-gradient(45deg, #808080 25%, transparent 25%), linear-gradient(-45deg, #808080 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #808080 75%), linear-gradient(-45deg, transparent 75%, #808080 75%)",
                                backgroundSize: "20px 20px",
                                backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                                borderRadius: (theme) => theme.radius.default,
                            }, className: linearGradientPickerClasses["transparent-background-container"], children: _jsx(Box, { ref: gradientRef, width: "100%", height: `${gradientBarHeight}px`, sx: {
                                    borderRadius: "inherit",
                                    backgroundImage: `linear-gradient(to right, ${gradient
                                        .map((color) => `${color.color} ${color.offset * 100}%`)
                                        .join(", ")})`,
                                }, className: linearGradientPickerClasses["linear-gradient"] }) }) }) })] })) }));
};
//# sourceMappingURL=LinearGradientPicker.js.map